import React, { useState, useEffect, useMemo } from 'react';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { useTitle } from '../../../context/TitleContext';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';

import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import { 
  FaPlus, 
  FaMapMarkerAlt, 
  FaStore, 
  FaEdit, 
  FaTrash, 
  FaSearch, 
  FaSort, 
  FaSortUp, 
  FaSortDown,
  FaFilter,
  FaEye,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';
import { getStoreBranches, deleteStoreBranch } from '../../../redux/Slices/storeBranchesSlice';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const StoreBranches = () => {
    const { setTitle } = useTitle();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { branches, pagination, loading, error } = useSelector((state) => state.storeBranches);
    const [updating, setUpdating] = useState(false);
    const [statusById, setStatusById] = useState({});
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', data: null });
    const [currentPage, setCurrentPage] = useState(1);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [pageInput, setPageInput] = useState(1);
    const [pageGroup, setPageGroup] = useState(0);

    // تحديد نوع المستخدم
    const getUserRole = () => {
        const storedStore = sessionStorage.getItem("storekhadamat");
        if (storedStore) {
            try {
                const parsed = JSON.parse(storedStore);
                return parsed.role;
            } catch {
                return 'store';
            }
        }
        return 'store';
    };

    const userRole = getUserRole();

    // دالة التعديل
    const handleEdit = (branch) => {
        // تخزين بيانات الفرع للتحرير
        sessionStorage.setItem('branchToEdit', JSON.stringify(branch));
        navigate('/store-branches/edit');
    };

    // دالة الحذف
    const handleDelete = async (branchId, branchName) => {
        setConfirmModal({
            isOpen: true,
            type: 'delete',
            data: { branchId, branchName },
            message: `هل أنت متأكد من حذف الفرع "${branchName}"؟`,
            title: 'تأكيد الحذف'
        });
    };

    // دالة تغيير حالة التفعيل
    const handleToggleStatus = async (branchId, currentStatus) => {
        const effectiveStatus = currentStatus ?? statusById[branchId] ?? '0';
        const newStatus = effectiveStatus === "1" || effectiveStatus === 1 ? 0 : 1;
        const confirmMessage = newStatus === 1 ? 'هل أنت متأكد من تفعيل هذا الفرع؟' : 'هل أنت متأكد من إلغاء تفعيل هذا الفرع؟';
        
        setConfirmModal({
            isOpen: true,
            type: 'toggleStatus',
            data: { branchId, newStatus },
            message: confirmMessage,
            title: newStatus === 1 ? 'تأكيد التفعيل' : 'تأكيد إلغاء التفعيل',
            confirmButtonColor: newStatus === 1 ? '#28a745' : '#ffc107'
        });
    };

    // Column helper for table configuration
    const columnHelper = createColumnHelper();

    // Table columns configuration
    const columns = useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: '#',
                cell: ({ row }) => (
                    <span className="fw-semibold text-muted">#{row.index + 1}</span>
                ),
                size: 80,
            }),
            columnHelper.accessor('market_name', {
                id: 'market_name',
                header: 'اسم الفرع',
                cell: ({ getValue }) => (
                    <div className="text-end" style={{ paddingRight: '30px' }}>
                        <h6 className="mb-0 fw-semibold">{getValue()}</h6>
                    </div>
                ),
                size: 300,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'الحالة',
                accessorFn: (row) => {
                    // استخدام statusById أو status الأصلي
                    return statusById[row.id] ?? row.status ?? '0';
                },
                cell: ({ row }) => {
                    const branchId = row.original.id;
                    const currentStatus = statusById[branchId] ?? row.original.status;
                    return (
                        <span className={`badge ${currentStatus === '1' ? 'bg-success' : 'bg-danger'} text-white`}>
                            {currentStatus === '1' ? 'نشط' : 'غير نشط'}
                        </span>
                    );
                },
                filterFn: (row, id, value) => {
                    const currentStatus = statusById[row.original.id] ?? row.original.status ?? '0';
                    return currentStatus === value;
                },
                size: 150,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'الإجراءات',
                cell: ({ row }) => {
                    const branch = row.original;
                    const currentStatus = statusById[branch.id] ?? branch.status;
                    return (
                        <div className="action-buttons d-flex gap-2 flex-wrap">
                            <button
                                className="btn btn-sm btn-action"
                                style={{ 
                                    backgroundColor: '#17a2b8', 
                                    borderColor: '#17a2b8', 
                                    color: 'white',
                                    fontSize: '12px',
                                    padding: '4px 8px',
                                    marginLeft: '8px'
                                }}
                                title="عرض التفاصيل"
                                onClick={() => navigate(`/store-branches/${branch.id}`)}
                            >
                                <FaEye className="mx-1" />
                                عرض
                            </button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-sm btn-toggle"
                                style={{ 
                                    backgroundColor: currentStatus === '1' ? '#dc3545' : '#28a745', 
                                    borderColor: currentStatus === '1' ? '#dc3545' : '#28a745', 
                                    color: '#fff',
                                    fontSize: '12px',
                                    padding: '4px 8px'
                                }}
                                title={currentStatus === '1' ? 'إلغاء التفعيل' : 'تفعيل'}
                                onClick={() => handleToggleStatus(branch.id, currentStatus)}
                                disabled={updating}
                            >
                                {currentStatus === '1' ? <FaToggleOff className="mx-1" /> : <FaToggleOn className="mx-1" />}
                                {currentStatus === '1' ? 'إلغاء التفعيل' : 'تفعيل'}
                            </motion.button>
                            <button
                                className="btn btn-sm btn-action"
                                style={{ 
                                    backgroundColor: '#184194', 
                                    borderColor: '#184194', 
                                    color: 'white',
                                    fontSize: '12px',
                                    padding: '4px 8px'
                                }}
                                title="تعديل"
                                onClick={() => handleEdit(branch)}
                            >
                                <FaEdit className="mx-1" />
                                تعديل
                            </button>
                            {branch.map_link && (
                                <a 
                                    href={branch.map_link} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="btn btn-sm"
                                    style={{ 
                                        backgroundColor: '#28a745', 
                                        borderColor: '#28a745', 
                                        color: 'white',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        padding: '4px 8px',
                                        textDecoration: 'none'
                                    }}
                                    title="عرض الموقع"
                                >
                                    <FaMapMarkerAlt className="mx-1" />
                                    خريطة
                                </a>
                            )}
                            <button
                                className="btn btn-sm btn-action"
                                style={{ 
                                    backgroundColor: '#dc3545', 
                                    borderColor: '#dc3545', 
                                    color: 'white',
                                    fontSize: '12px',
                                    padding: '4px 8px'
                                }}
                                title="حذف"
                                onClick={() => handleDelete(branch.id, branch.market_name)}
                            >
                                <FaTrash className="mx-1" />
                                حذف
                            </button>
                        </div>
                    );
                },
                size: 500,
            }),
        ],
        [statusById, updating, navigate, handleEdit, handleToggleStatus, handleDelete]
    );

    // Table configuration
    const table = useReactTable({
        data: branches || [],
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    useEffect(() => {
        const title = userRole === 'pharmacy' ? 'فروع الصيدليات' : 'فروع المتجر';
        setTitle(title);
        dispatch(getStoreBranches());
    }, [setTitle, dispatch, userRole]);

    // تحديث pageInput عند تغيير currentPage
    useEffect(() => {
        setPageInput(currentPage);
    }, [currentPage]);

    // دالة التنقل بالصفحات عبر input
    const handlePageInputChange = (e) => {
        const value = parseInt(e.target.value);
        if (!isNaN(value) && value >= 1) {
            setPageInput(value);
        }
    };

    const handlePageInputSubmit = (e) => {
        e.preventDefault();
        if (pagination && pageInput >= 1 && pageInput <= pagination.pages_count) {
            setCurrentPage(pageInput);
        }
    };

    // دالة تغيير الصفحة
    const handlePageChange = (page) => {
        if (pagination && page >= 1 && page <= pagination.pages_count) {
            setCurrentPage(page);
            
            // تحديث pageGroup بناءً على الصفحة المختارة
            const totalPages = pagination.pages_count || 1;
            const pagesPerGroup = Math.ceil(totalPages / 2);
            const newPageGroup = Math.floor((page - 1) / pagesPerGroup);
            setPageGroup(newPageGroup);
            
            // يمكن إضافة API call هنا إذا كان مطلوباً
        }
    };

    const renderPagination = () => {
        if (!pagination) return null;

        return (
            <EnhancedPagination
                currentPage={currentPage}
                totalPages={pagination.pages_count}
                onPageChange={handlePageChange}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                showInfo={false}
                className="mt-4"
            />
        );
    };

    // جلب حالة كل فرع من تفاصيل الفرع لأن قائمة العرض لا تحتوي على status
    useEffect(() => {
        const fetchStatuses = async () => {
            if (!branches || !Array.isArray(branches) || branches.length === 0) return;
            const token = getAuthToken();
            if (!token) return;

            const updates = {};
            await Promise.all(
                branches.map(async (b) => {
                    try {
                        // إذا كانت الحالة موجودة مسبقًا في الخريطة، تخطى
                        if (statusById[b.id] !== undefined) return;
                        const resp = await axios.get(`${BASE_URL}/stores/store_branches/${b.id}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept-Language': 'ar',
                                'lang': 'ar',
                            },
                        });
                        const statusVal = resp?.data?.result?.status ?? resp?.data?.data?.status;
                        if (statusVal !== undefined) {
                            updates[b.id] = String(statusVal);
                        }
                    } catch (e) {
                        // تجاهل خطأ فرعي حتى لا يمنع بقية التحديثات
                    }
                })
            );
            if (Object.keys(updates).length > 0) {
                setStatusById((prev) => ({ ...prev, ...updates }));
            }
        };

        fetchStatuses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branches]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
        }
    }, [error]);

    const executeDelete = async () => {
        try {
            await dispatch(deleteStoreBranch(confirmModal.data.branchId)).unwrap();
            showSuccessToast('تم حذف الفرع بنجاح');
            dispatch(getStoreBranches());
        } catch (error) {
            showErrorToast('فشل في حذف الفرع');
        }
    };

    const executeToggleStatus = async ({ branchId, newStatus }) => {
        try {
            setUpdating(true);
            const token = getAuthToken();
            const response = await axios.post(`${BASE_URL}/stores/store_branches/${branchId}/change_status`, {
                status: newStatus
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            const apiStatus = response.data?.status;
            if (apiStatus === 'success' || apiStatus === 200) {
                showSuccessToast(newStatus === 1 ? 'تم تفعيل الفرع بنجاح' : 'تم إلغاء تفعيل الفرع بنجاح');
                setStatusById((prev) => ({ ...prev, [branchId]: String(newStatus) }));
                dispatch(getStoreBranches());
            } else {
                throw new Error(response.data.message || 'فشل في تغيير حالة الفرع');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'فشل في تغيير حالة الفرع');
        } finally {
            setUpdating(false);
        }
    };

    const handleConfirmAction = () => {
        if (confirmModal.type === 'delete') {
            executeDelete();
        } else if (confirmModal.type === 'toggleStatus') {
            executeToggleStatus(confirmModal.data);
        }
    };

    // دالة الحصول على التوكن
    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const LoadingSpinner = () => (
        <div className="spinner-border text-light" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
            <span className="visually-hidden">Loading...</span>
        </div>
    );

    return (
        <div className="main_content">
            

            <div className="dashboard-container content-wrapper">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="mb-0" style={{fontSize: '20px'}}>{userRole === 'pharmacy' ? 'فروع الصيدليات' : 'فروع المتجر'}</h2>
                      
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-primary d-flex align-items-center"
                        onClick={() => navigate('/store-branches/add')}
                        style={{
                            backgroundColor: '#184194',
                            border: 'none',
                            borderRadius: '8px'
                        }}>
                        <FaPlus className="mx-2" />
                        {userRole === 'pharmacy' ? 'إضافة فرع صيدلية جديد' : 'إضافة فرع جديد'}
                    </motion.button>
                </div>

                {/* Search and Filter Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern mb-4"
                >
                    <div className="card-body p-3">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="search-container position-relative">
                                    <div className="input-group search-input-group">
                                        <span className="input-group-text search-icon">
                                            <FaSearch className="text-white" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control search-input"
                                            placeholder="البحث في الفروع..."
                                            value={globalFilter ?? ''}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6 text-end">
                                <div className="d-flex gap-2 justify-content-end align-items-center">
                                    {(globalFilter || columnFilters.length > 0) && (
                                        <span className="badge bg-info text-white mx-2">
                                            {table.getFilteredRowModel().rows.length} من {branches?.length || 0} نتيجة
                                        </span>
                                    )}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        <FaFilter className="mx-1" />
                                        {showFilters ? 'إخفاء المرشحات' : 'إظهار المرشحات'}
                                    </button>
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            setGlobalFilter('');
                                            setColumnFilters([]);
                                            setSorting([]);
                                        }}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        مسح الكل
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        {/* Advanced Filters */}
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3 pt-3 border-top"
                            >
                                <div className="row">
                                    <div className="col-md-12">
                                        <label className="form-label fw-semibold">حالة الفرع</label>
                                        <select
                                            className="form-select"
                                            value={columnFilters.find(f => f.id === 'status')?.value ?? ''}
                                            onChange={(e) => {
                                                const statusFilter = e.target.value;
                                                setColumnFilters(prev => 
                                                    prev.filter(f => f.id !== 'status').concat(
                                                        statusFilter ? [{ id: 'status', value: statusFilter }] : []
                                                    )
                                                );
                                            }}
                                        >
                                            <option value="">جميع الحالات</option>
                                            <option value="1">نشط</option>
                                            <option value="0">غير نشط</option>
                                        </select>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </motion.div>

                {/* Modern Data Table */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-sm card-modern"
                >
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <LoadingSpinner />
                            </div>
                        ) : branches && Array.isArray(branches) && branches.length > 0 ? (
                            <div className="table-responsive" style={{ 
                                overflowX: 'auto', 
                                overflowY: 'visible',
                                maxHeight: 'none'
                            }}>
                                <table className="table mb-0 table-modern" style={{ 
                                    backgroundColor: 'transparent', 
                                    '--bs-table-striped-bg': 'transparent',
                                    '--bs-table-hover-bg': 'rgba(0,0,0,0.05)',
                                    minWidth: '1200px',
                                    width: '100%'
                                }}>
                                    <thead className="table-light">
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(header => (
                                                    <th
                                                        key={header.id}
                                                        className="border-0 fw-semibold text-center"
                                                        style={{ 
                                                            backgroundColor: 'transparent',
                                                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                            userSelect: 'none',
                                                            width: header.getSize()
                                                        }}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                            {header.column.getCanSort() && (
                                                                <span>
                                                                    {header.column.getIsSorted() === 'asc' ? (
                                                                        <FaSortUp className="text-primary" />
                                                                    ) : header.column.getIsSorted() === 'desc' ? (
                                                                        <FaSortDown className="text-primary" />
                                                                    ) : (
                                                                        <FaSort className="text-muted" />
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                        </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map((row, index) => (
                                            <motion.tr
                                                key={row.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                                style={{ backgroundColor: 'transparent' }}
                                                className="table-row-hover"
                                            >
                                                {row.getVisibleCells().map(cell => (
                                                    <td
                                                        key={cell.id}
                                                        className="border-0 text-center align-middle"
                                                            style={{ 
                                                            backgroundColor: 'transparent',
                                                            width: cell.column.getSize()
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                                ))}
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <div className="text-muted">
                                    <FaStore size={48} className="mb-3" />
                                    <p>لا توجد فروع حالياً</p>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Table Pagination */}
                    {table.getPageCount() > 1 && (
                        <div className="card-footer bg-light">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted">عرض</span>
                                        <select
                                            className="form-select form-select-sm"
                                            style={{ width: 'auto' }}
                                            value={table.getState().pagination.pageSize}
                                            onChange={(e) => table.setPageSize(Number(e.target.value))}
                                        >
                                            {[5, 10, 20, 30, 50].map(pageSize => (
                                                <option key={pageSize} value={pageSize}>
                                                    {pageSize}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="text-muted">
                                            من {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} إلى{' '}
                                            {Math.min(
                                                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                                table.getFilteredRowModel().rows.length
                                            )}{' '}
                                            من أصل {table.getFilteredRowModel().rows.length} نتيجة
                                        </span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-end gap-2">
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(0)}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            الأولى
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.previousPage()}
                                            disabled={!table.getCanPreviousPage()}
                                        >
                                            السابقة
                                        </button>
                                        <span className="d-flex align-items-center px-3">
                                            صفحة {table.getState().pagination.pageIndex + 1} من {table.getPageCount()}
                                        </span>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.nextPage()}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            التالية
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                            disabled={!table.getCanNextPage()}
                                        >
                                            الأخيرة
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* API Pagination */}
                {pagination && (pagination.total > 0 || branches?.length > 0) && (
                    <div className="card-footer bg-light">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="text-muted">الانتقال إلى الصفحة</span>
                                    <div className="input-group" style={{ width: 'auto' }}>
                                        <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                            #
                                        </span>
                                                <input
                                                    type="number"
                                            className="form-control form-control-sm"
                                            value={currentPage}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                if (!isNaN(value) && value > 0) {
                                                    const totalPages = pagination?.pages_count || 1;
                                                    if (value <= totalPages) {
                                                        handlePageChange(value);
                                                    }
                                                }
                                                    }}
                                                    min="1"
                                            max={pagination?.pages_count || 1}
                                            style={{ width: '60px' }}
                                        />
                                                </div>
                                            </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2 flex-wrap">
                                            <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage <= 1}
                                    >
                                        الأولى
                                            </button>
                                        <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                    >
                                        السابقة
                                        </button>
                                    <span className="d-flex align-items-center px-3">
                                        صفحة {pagination.current_page || currentPage} من {pagination.pages_count || 1}
                                    </span>
                                            <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage >= (pagination.pages_count || 1)}
                                    >
                                        التالية
                                            </button>
                                        <button 
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => handlePageChange(pagination.pages_count || 1)}
                                        disabled={currentPage >= (pagination.pages_count || 1)}
                                    >
                                        الأخيرة
                                        </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, type: '', data: null })}
                onConfirm={handleConfirmAction}
                title={confirmModal.title || 'تأكيد العملية'}
                message={confirmModal.message || 'هل أنت متأكد من هذا الإجراء؟'}
                confirmText="تأكيد"
                cancelText="إلغاء"
                confirmButtonColor={confirmModal.confirmButtonColor || '#dc3545'}
                type={confirmModal.type === 'delete' ? 'danger' : 'warning'}
            />
        </div>
    );
};

export default StoreBranches;

// CSS للجدول المحسن
const styles = `
.btn-toggle:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.table-modern {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    min-width: 1200px;
}

.table-modern thead th {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    color: #495057;
    padding: 1rem 0.75rem;
    position: sticky;
    top: 0;
    z-index: 10;
}

.table-modern tbody tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f1f3f4;
}

.table-modern tbody tr:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.table-modern tbody tr:last-child {
    border-bottom: none;
}

.table-modern td {
    padding: 1rem 0.75rem;
    vertical-align: middle;
    white-space: normal;
    word-wrap: break-word;
    max-width: 200px;
    min-width: 120px;
}

.action-buttons .btn {
    margin: 2px;
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.2s ease;
    min-width: 80px;
}

.action-buttons .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.card-modern {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: hidden;
}

.card-modern .card-body {
    padding: 1.5rem;
}

.table-responsive {
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
    max-height: none;
}

/* Custom Horizontal Scrollbar for Table */
.table-responsive::-webkit-scrollbar {
    height: 8px;
    width: 0;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border-radius: 4px;
    transition: all 0.3s ease;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%);
    transform: scale(1.1);
}

/* Firefox Horizontal Scrollbar */
.table-responsive {
    scrollbar-width: thin;
    scrollbar-color: #184194 #f1f1f1;
}

/* Search Input Styling */
.search-container {
    position: relative;
}

.search-input-group {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.search-input-group:focus-within {
    box-shadow: 0 4px 16px rgba(24, 65, 148, 0.2);
    transform: translateY(-1px);
}

.search-icon {
    background: linear-gradient(135deg, #184194 0%, #2c5aa0 100%);
    border: none;
    color: white;
    padding: 12px 16px;
    font-size: 16px;
}

.search-input {
    border: none;
    padding: 12px 16px;
    font-size: 14px;
    background: white;
    transition: all 0.3s ease;
}

.search-input:focus {
    border: none;
    box-shadow: none;
    background: #f8f9fa;
}

.search-input::placeholder {
    color: #6c757d;
    font-style: italic;
}


.input-group .form-control:focus {
    border-color: #184194;
    box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.25);
}

.btn-outline-secondary:hover {
    background-color: #6c757d;
    border-color: #6c757d;
}

.btn-outline-primary:hover {
    background-color: #184194;
    border-color: #184194;
}

.table-row-hover:hover {
    background-color: rgba(24, 65, 148, 0.05) !important;
}

/* Original Pagination Styling */
.pagination {
    margin-bottom: 0;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem;
}

.pagination .page-item {
    margin: 0.125rem;
}

.pagination .page-link {
    color: #184194;
    border-color: #dee2e6;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    margin: 0 2px;
    transition: all 0.2s ease;
}

.pagination .page-link:hover {
    color: #fff;
    background-color: #184194;
    border-color: #184194;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(24, 65, 148, 0.3);
}

.pagination .page-item.active .page-link {
    background-color: #184194;
    border-color: #184194;
    color: white;
}

.pagination .page-item.disabled .page-link {
    color: #6c757d;
    background-color: #fff;
    border-color: #dee2e6;
    cursor: not-allowed;
}

/* Page Input Navigation Styling */
.page-input-container {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    padding: 0.75rem 1rem;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    backdrop-filter: blur(10px);
}

.page-input-form {
    background: transparent;
    padding: 0;
    border: none;
    box-shadow: none;
}

.page-input-custom {
    border: 2px solid #e9ecef !important;
    border-radius: 8px !important;
    text-align: center !important;
    font-weight: 600 !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
    transition: all 0.3s ease !important;
}

.page-input-custom:focus {
    border-color: #184194 !important;
    box-shadow: 0 0 0 0.2rem rgba(24, 65, 148, 0.25), 0 4px 8px rgba(24, 65, 148, 0.15) !important;
    transform: translateY(-1px) !important;
    background: #ffffff !important;
}

.page-input-icon {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 12px;
    pointer-events: none;
}

.page-go-btn {
    border-radius: 8px !important;
    font-weight: 600 !important;
    transition: all 0.3s ease !important;
    position: relative;
    overflow: hidden;
}

.page-go-btn:hover:not(:disabled) {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 12px rgba(24, 65, 148, 0.4) !important;
    background: linear-gradient(135deg, #0f2a6b 0%, #1e4a8c 100%) !important;
}

.page-go-btn:active:not(:disabled) {
    transform: translateY(0) !important;
    box-shadow: 0 2px 4px rgba(24, 65, 148, 0.3) !important;
}

.page-go-btn:disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
    transform: none !important;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}

.page-go-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
}

.page-go-btn:hover::before {
    left: 100%;
}

@media (max-width: 768px) {
    .action-buttons {
        flex-direction: column;
        gap: 4px;
    }
    
    .action-buttons .btn {
        width: 100%;
        font-size: 11px;
        padding: 4px 6px;
    }
    
    .table-modern {
        min-width: 800px;
    }
    
    .table-modern th,
    .table-modern td {
        padding: 0.5rem 0.25rem;
        font-size: 0.875rem;
        max-width: 150px;
        min-width: 80px;
    }
    
    .search-input-group {
        margin-bottom: 1rem;
    }
    
    .pagination {
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.25rem;
    }
    
    .pagination .page-item {
        margin: 0.125rem;
    }
    
    .pagination .page-link {
        padding: 0.375rem 0.5rem;
        font-size: 0.875rem;
    }
    
    .search-input {
        font-size: 16px; /* منع التكبير في iOS */
    }
}
`;

// إضافة الـ CSS للصفحة
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}
